import { ConfigProvider, Select, Tabs, TabsProps } from 'antd';
import React, { useState, useMemo } from 'react';
import { Chart } from '@mm/components';
import styles from './SignalGraph.module.css';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';

export const signalRangeSelectOptions = [
  {
    value: 'week' as const,
    label: 'Last Week',
  },
  {
    value: 'month' as const,
    label: 'Last Month',
  },
  {
    value: '3 months' as const,
    label: 'Last 3 Months',
  },
];

export const SignalGraph = ({
  selectedRangeOption,
  setSelectedRangeOption,
  data,
}: {
  selectedRangeOption: string;
  setSelectedRangeOption: (
    value: typeof signalRangeSelectOptions[0]['value'],
  ) => void;
  data: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      borderColor: string;
      fill: boolean;
    }[];
  } | null;
}) => {
  const [activeTab, setActiveTab] = useState('1');
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'All Events',
    },
    {
      key: '2',
      label: 'Product View',
    },
    {
      key: '3',
      label: 'Collection View',
    },
    {
      key: '4',
      label: 'Add to Cart',
    },
    {
      key: '5',
      label: 'Return Visit',
    },
  ];

  const { isWhitelabelCompany, primaryColor } = useWhitelabelConfig();

  const tabsColor = isWhitelabelCompany
    ? primaryColor
    : 'var(--color-geekblue-6)';

  const chartData = useMemo(() => {
    const labels = data?.labels ?? [];
    const chartData = {
      labels,
      data: [] as (string | number)[][],
      color: [] as string[],
      title: [] as string[],
      fill: false,
    };

    if (data) {
      switch (activeTab) {
        case '2': {
          const productViewData = data.datasets.find(
            d => d.label === 'Product View',
          );
          if (productViewData) {
            chartData.data = [productViewData.data];
            chartData.color = [productViewData.borderColor];
          }
          chartData.title = ['Product View'];
          break;
        }
        case '3': {
          const collectionViewData = data.datasets.find(
            d => d.label === 'Collection View',
          );
          if (collectionViewData) {
            chartData.data = [collectionViewData.data];
            chartData.color = [collectionViewData.borderColor];
          }
          chartData.title = ['Collection View'];
          break;
        }
        case '4': {
          const addToCartData = data.datasets.find(
            d => d.label === 'Add to Cart',
          );
          if (addToCartData) {
            chartData.data = [addToCartData.data];
            chartData.color = [addToCartData.borderColor];
          }
          chartData.title = ['Add to Cart'];
          break;
        }
        case '5': {
          const returnVisitData = data.datasets.find(
            d => d.label === 'Return Visit',
          );
          if (returnVisitData) {
            chartData.data = [returnVisitData.data];
            chartData.color = [returnVisitData.borderColor];
          }
          chartData.title = ['Return Visit'];
          break;
        }
        default: {
          const allEventsData = data.datasets.find(
            d => d.label === 'All Events',
          );
          if (allEventsData) {
            chartData.data = [allEventsData.data];
            chartData.color = [allEventsData.borderColor];
          }
          chartData.title = ['All Events'];
        }
      }
    }
    return chartData;
  }, [activeTab, data?.labels, data?.datasets]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>Visitor event data tracking</div>
        <Select
          defaultValue={selectedRangeOption}
          onChange={setSelectedRangeOption}
          options={signalRangeSelectOptions}
          popupMatchSelectWidth={false}
        />
      </div>
      <div className={styles.graphContainer}>
        <ConfigProvider
          theme={{
            components: {
              Tabs: {
                colorPrimary: tabsColor,
                colorPrimaryHover: tabsColor,
              },
            },
          }}
        >
          <Tabs
            items={items}
            onChange={key => setActiveTab(key)}
            activeKey={activeTab}
          />
          <Chart
            className={styles.chart}
            data={chartData}
            type="line"
            additionalOpts={{
              // @ts-ignore
              datasetOpts: { pointRadius: 4, circleFilled: true },
              otherOpts: {
                legend: {
                  display: true,
                  align: 'center',
                  position: 'bottom',
                  labels: {
                    usePointStyle: true,
                    boxWidth: 8,
                    padding: 16,
                  },
                },
              },
            }}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};
